import * as React from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { Card, CardContent } from '../../../components/ui/Card.js';
import { Button } from '../../../components/ui/Button.js';
import { Input } from '../../../components/ui/FormField.js';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { Money } from '../../../components/ui/Money';
import { useToast } from '../../../components/ui/Toast.js';
import { EntityActionMenu } from '../../../components/ui/EntityActionMenu.js';
import { Plus, Search, RotateCcw, TrendingUp, Users, Calendar, Eye } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { PageHeader } from '../../../components/layout/PageHeader';
import { usePidiSchemes, usePidiCollections, useReversePidiCollection } from '../../../features/pidi/network';
import { usePageTitle } from '../../../lib/hooks';

export const Route = createFileRoute('/_auth/pidi-schemes/')({
  component: PidiSchemesPage,
});

function PidiSchemesPage() {
  usePageTitle('Pidi Daily Schemes');
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState<'schemes' | 'collections'>('schemes');

  const [schemeQuery, setSchemeQuery] = React.useState('');
  const [collectionQuery, setCollectionsQuery] = React.useState('');

  const { data: schemesResponse } = usePidiSchemes(schemeQuery ? { query: schemeQuery } : undefined);
  const { data: collectionsResponse } = usePidiCollections(collectionQuery ? { query: collectionQuery } : undefined);
  const reverseCollection = useReversePidiCollection();

  const schemes = schemesResponse?.data ?? [];
  const collections = collectionsResponse?.data ?? [];

  const totalPaiseCollected = schemes.reduce((acc, s) => acc + s.collectedAmount, 0);
  const activeAgents = Array.from(new Set(schemes.map(s => s.agentName))).length;

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Pidi Daily Chits"
        subtitle="Manage micro-deposit schemes, daily collection agents, and high-frequency cash receipts."
        action={
          <Button variant="primary" onClick={() => navigate({ to: '/pidi-schemes/new' })}>
            <Plus className="w-4 h-4 mr-2" />
            Create Pidi Scheme
          </Button>
        }
      />
      <div className="flex-1 w-full min-h-0 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Total Micro-Collections</span>
                <h2 className="text-2xl font-bold text-text-primary">
                  <Money paise={totalPaiseCollected} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                </h2>
              </div>
              <div className="p-3 bg-success-bg text-success-500 rounded-lg">
                <TrendingUp className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Active Daily Schemes</span>
                <h2 className="text-2xl font-bold text-text-primary">
                  {schemes.filter(s => s.status === 'ACTIVE').length} / {schemes.length}
                </h2>
              </div>
              <div className="p-3 bg-info-bg text-info-text rounded-lg">
                <Calendar className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Assigned Agents</span>
                <h2 className="text-2xl font-bold text-text-primary">{activeAgents} Agents</h2>
              </div>
              <div className="p-3 bg-warning-bg text-warning-text rounded-lg">
                <Users className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-4 border-b border-border">
          <Button
            variant="ghost"
            onClick={() => setActiveTab('schemes')}
            className={`pb-2 px-1 text-sm font-semibold transition-colors border-b-2 ${
              activeTab === 'schemes' ? 'border-accent-500 text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            Daily Schemes
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActiveTab('collections')}
            className={`pb-2 px-1 text-sm font-semibold transition-colors border-b-2 ${
              activeTab === 'collections' ? 'border-accent-500 text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            Agent Collections Register
          </Button>
        </div>

        {activeTab === 'schemes' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
                <Input
                  type="text"
                  placeholder="Search scheme number, display name, agent..."
                  value={schemeQuery}
                  onChange={e => setSchemeQuery(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
                />
              </div>
            </div>

            <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
              <table className="w-full border-collapse text-left text-sm">
                <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                  <tr>
                    <th className="p-4">Scheme Info</th>
                    <th className="p-4">Daily Rate</th>
                    <th className="p-4">Duration (Days)</th>
                    <th className="p-4">Assigned Agent</th>
                    <th className="p-4">Total Collected</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Timeline</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-text-primary">
                  {schemes.map(s => (
                    <tr key={s.id} className="hover:bg-surface-2 transition-colors">
                      <td className="p-4">
                        <div className="flex flex-col">
                          <Link
                            to="/pidi-schemes/$id"
                            params={{ id: s.id }}
                            className="font-semibold text-accent-500 hover:underline"
                          >
                            {s.displayName}
                          </Link>
                          <span className="font-mono text-xs text-text-muted mt-0.5">{s.schemeNumber}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <Money paise={s.dailyAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                      </td>
                      <td className="p-4 font-mono">{s.durationDays} Days</td>
                      <td className="p-4">{s.agentName}</td>
                      <td className="p-4 font-semibold text-success-text">
                        <Money paise={s.collectedAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                      </td>
                      <td className="p-4">
                        <StatusBadge
                          status={s.status === 'ACTIVE' ? 'success' : s.status === 'CLOSED' ? 'danger' : 'info'}
                          label={s.status}
                        />
                      </td>
                      <td className="p-4 text-xs text-text-secondary font-mono">
                        {s.startDate} to {s.endDate}
                      </td>
                      <td className="p-4 text-right">
                        <EntityActionMenu
                          actions={[
                            {
                              label: 'View Scheme Details',
                              icon: <Eye className="w-4 h-4" />,
                              onClick: () => navigate({ to: '/pidi-schemes/$id', params: { id: s.id } }),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                  {schemes.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-text-muted">No pidi daily schemes found matching criteria.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'collections' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
                <Input
                  type="text"
                  placeholder="Search subscriber, receipt number, scheme..."
                  value={collectionQuery}
                  onChange={e => setCollectionsQuery(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
                />
              </div>
            </div>

            <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
              <table className="w-full border-collapse text-left text-sm">
                <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                  <tr>
                    <th className="p-4">Receipt No.</th>
                    <th className="p-4">Scheme Number</th>
                    <th className="p-4">Subscriber</th>
                    <th className="p-4">Amount Collected</th>
                    <th className="p-4">Collected At</th>
                    <th className="p-4">Collection Agent</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-text-primary">
                  {collections.map(c => (
                    <tr key={c.id} className="hover:bg-surface-2 transition-colors">
                      <td className="p-4 font-mono text-xs">{c.receiptNumber}</td>
                      <td className="p-4 font-mono text-xs">{c.schemeNumber}</td>
                      <td className="p-4 font-semibold">{c.customerName}</td>
                      <td className="p-4">
                        <Money paise={c.amount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                      </td>
                      <td className="p-4 font-mono text-xs">
                        {new Date(c.collectedAt).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="p-4">{c.agentName}</td>
                      <td className="p-4">
                        <StatusBadge
                          status={c.status === 'SUCCESS' ? 'success' : 'danger'}
                          label={c.status}
                        />
                      </td>
                      <td className="p-4 text-right">
                        <EntityActionMenu
                          actions={[
                            {
                              label: 'Reverse Collection',
                              icon: <RotateCcw className="w-4 h-4" />,
                              variant: 'destructive',
                              requiresConfirmation: true,
                              confirmTitle: 'Reverse Collection',
                              confirmDescription: `Are you sure you want to reverse collection receipt ${c.receiptNumber}? This will mark the transaction as reversed.`,
                              confirmLabel: 'Reverse Collection',
                              hidden: c.status !== 'SUCCESS',
                              onClick: async () => {
                                try {
                                  await reverseCollection.mutateAsync(c.id);
                                  toast({
                                    type: 'success',
                                    title: 'Collection Reversed',
                                    description: 'The daily collection transaction has been marked as REVERSED.',
                                  });
                                } catch (err) {
                                  console.error('Failed to reverse collection:', err);
                                  toast({
                                    type: 'error',
                                    title: 'Reversal Failed',
                                    description: err instanceof Error ? err.message : 'Failed to reverse collection.',
                                  });
                                }
                              },
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                  {collections.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-text-muted">No daily collection records found matching search.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
